import { ActionSheetContext } from '@alto/design-system';
import {
  type LineItemInfoKey,
  type LineItemType,
  LineItemTypeMap,
} from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import React, { useContext } from 'react';
import { getHasScheduleableDeliveryFeesInCart } from '~shared/features/checkout/selectors/getCheckout';
import { getOrderAddressId, getOrderDate } from '~shared/features/checkout/selectors/getOrder';
import {
  NEW_ESTIMATED_PREFIX_COPY,
  PRICE_FREE,
  PRICING_LABELS,
  TOTAL_PRICE_PENDING,
} from '~shared/features/pricing/constants';
import { getIsGskForYouUser } from '~shared/features/users/selectors/getIsGskForYouUser';
import { isDeliveryOrMailFeeLineItem, isItemTypeExpeditedDeliveryFee } from '~shared/helpers/pricingBreakdown';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';
import { FeeDetailsActionSheet } from '../../checkout/components/select-date-card/FeeDetailsActionSheet';
import { DispensingFeeDetailsActionSheet } from './DispensingFeeDetailsActionSheet';
import { EstimatedPriceActionSheet } from './EstimatedPriceActionSheet';
import { PricingInfoRow } from './PricingInfoRow';

type Props = {
  readonly infoKey?: LineItemInfoKey | null;
  readonly itemType: LineItemType;
  readonly itemValue: number | null | undefined;
  readonly label: string | null | undefined;
  readonly openEditTip?: () => void;
  readonly originalValue?: number | null;
  readonly showEditTipOption?: boolean;
  readonly showEstimatedTotal?: boolean;
  readonly totalItemCount: number;
  readonly origin: 'Cart' | 'Checkout' | 'DeliveryDetails';
};

export const PricingInfoLineItem = ({
  infoKey,
  itemType,
  itemValue,
  label,
  openEditTip,
  originalValue,
  showEditTipOption,
  showEstimatedTotal,
  totalItemCount,
  origin,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: Props) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const orderDate = useSelectorShared(getOrderDate);
  const addressID = useSelectorShared(getOrderAddressId);
  const isGskForYouUser = useSelectorShared(getIsGskForYouUser);
  const isCartOrCheckout = ['Cart', 'Checkout'].includes(origin);
  const hasScheduleableDeliveryFeesInCart = useSelectorShared(getHasScheduleableDeliveryFeesInCart);
  const isEstimatedAmount = infoKey === 'estimated' || (itemType === 'total' && !!showEstimatedTotal);
  const lineItemLabel = `${isEstimatedAmount ? NEW_ESTIMATED_PREFIX_COPY : ''}${label}`;
  const savings = PRICING_LABELS[itemType].savings;
  const hasFee = (itemValue ?? 0) > 0;
  const hasNextDayMailFee = itemType === LineItemTypeMap.NEXT_DAY_MAIL_FEE && hasFee;
  const showGskForYouFeeInfo = isGskForYouUser && hasNextDayMailFee;

  const handlePressGskForYouFeeInfo = () => {
    setActiveActionSheet(<FeeDetailsActionSheet />);
  };

  const handlePressProgramDispensingFeeDetails = () => {
    trackEvent({ event: EVENTS.DISPENSING_FEE_INFO_TAPPED, params: { origin } });
    setActiveActionSheet(<DispensingFeeDetailsActionSheet />);
  };

  // In cart/checkout, hide delivery fee line items until an (default) address and order date are selected.
  // After that, show specific delivery fee lint item if scheduleable, or generic delivery fee line item if not schedulable
  const isDeliveryFeeLineItem = isDeliveryOrMailFeeLineItem(itemType);
  const isScheduleableDeliveryFeeLineItem = isItemTypeExpeditedDeliveryFee(itemType);
  const shouldHideLineItem =
    isCartOrCheckout &&
    isDeliveryFeeLineItem &&
    (!addressID || !orderDate || (isScheduleableDeliveryFeeLineItem && !hasScheduleableDeliveryFeesInCart));

  if (shouldHideLineItem) {
    return null;
  }

  if ('delivery_fee' === itemType) {
    return (
      <PricingInfoRow
        label={lineItemLabel}
        price={itemValue || PRICE_FREE}
        savings={savings}
      />
    );
  }
  if (isItemTypeExpeditedDeliveryFee(itemType)) {
    return (
      <PricingInfoRow
        label={lineItemLabel}
        originalPrice={originalValue}
        price={itemValue || PRICE_FREE}
        savings={savings}
        onPressMoreInfo={showGskForYouFeeInfo ? handlePressGskForYouFeeInfo : undefined}
      />
    );
  }

  if ('tip_amount' === itemType) {
    return (
      <PricingInfoRow
        label={lineItemLabel}
        onPressChange={showEditTipOption ? openEditTip : undefined}
        price={itemValue}
        savings={savings}
      />
    );
  }

  if ('pickup' === itemType) {
    return (
      <PricingInfoRow
        label={lineItemLabel}
        price={PRICE_FREE}
      />
    );
  }

  if ('total' === itemType) {
    if ('price_unavailable' === infoKey) {
      return (
        <PricingInfoRow
          label={lineItemLabel}
          price={TOTAL_PRICE_PENDING}
          savings={savings}
        />
      );
    }

    if ('estimated' === infoKey) {
      const handlePressMoreInfo = () => {
        setActiveActionSheet(<EstimatedPriceActionSheet totalItemCount={totalItemCount} />);
      };
      return (
        <PricingInfoRow
          onPressMoreInfo={handlePressMoreInfo}
          label={lineItemLabel}
          price={itemValue}
          savings={savings}
        />
      );
    }
  }

  if (itemType === LineItemTypeMap.TOTAL_PROGRAM_DISPENSING_FEE && hasFee) {
    return (
      <PricingInfoRow
        label={lineItemLabel}
        price={itemValue}
        savings={savings}
        onPressMoreInfo={handlePressProgramDispensingFeeDetails}
      />
    );
  }

  return (
    <PricingInfoRow
      label={lineItemLabel}
      price={itemValue}
      savings={savings}
    />
  );
};
